import { z } from "zod";

/**
 * Event-day/post-event media assets (Phase 6) — the capture checklist's
 * artifacts, tracked per-event with host-delivery and posting-permission
 * state. `r2_key` is a plain reference string in v1 (no upload UI yet — the
 * dialog captures a reference the way ContractCard captures `contract_key`
 * for the contract file; a real R2 upload flow is a later phase).
 */

export const AssetKindSchema = z.enum([
  "hero_photo",
  "reveal_clip",
  "reaction_clip",
  "na_parity",
  "gallery",
  "recap_reel",
]);
export type AssetKind = z.infer<typeof AssetKindSchema>;

export const ASSET_KIND_LABELS: Record<AssetKind, string> = {
  hero_photo: "Hero photo",
  reveal_clip: "Reveal clip",
  reaction_clip: "Reaction clip",
  na_parity: "NA-parity shot",
  gallery: "Gallery",
  recap_reel: "Recap reel",
};

export const PostPermissionSchema = z.enum(["unasked", "granted", "denied"]);
export type PostPermission = z.infer<typeof PostPermissionSchema>;

export const POST_PERMISSION_LABELS: Record<PostPermission, string> = {
  unasked: "Not asked",
  granted: "Granted",
  denied: "Denied",
};

export const POST_PERMISSION_BADGE_VARIANT: Record<
  PostPermission,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  unasked: "secondary",
  granted: "success",
  denied: "danger",
};

export const AssetSchema = z.object({
  id: z.string(),
  event_id: z.string(),
  kind: AssetKindSchema,
  r2_key: z.string(),
  caption: z.string().nullable(),
  delivered_to_host: z.boolean(),
  post_permission: PostPermissionSchema,
  created_at: z.string(),
});
export type Asset = z.infer<typeof AssetSchema>;

export const AssetListResponseSchema = z.object({
  items: z.array(AssetSchema),
});
export type AssetListResponse = z.infer<typeof AssetListResponseSchema>;

// ---- Mutation inputs -------------------------------------------------------

export interface CreateAssetInput {
  eventId: string;
  kind: AssetKind;
  r2_key: string;
  caption?: string | null;
}

export interface PatchAssetInput {
  eventId: string;
  assetId: string;
  delivered_to_host?: boolean;
  post_permission?: PostPermission;
  caption?: string | null;
}

export interface DeleteAssetInput {
  eventId: string;
  assetId: string;
}
